import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { ElectricityConnectedTo } from "shared/types/electricity";
import { FileLabel, FileLogStatus } from "shared/types/file";
import { MeterType } from "shared/types/meter";

// Calculate the date for 3 months from now
const threeMonthsFromNow = new Date();
threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);

export const calculateElectricityDotValues = async (unit: any, session: any) => {
    let dotValues = {
        red: { count: 0, labels: [] as string[] },
        orange: { count: 0, labels: [] as string[] },
        green: { count: 0, labels: [] as string[] },
        transparent: { count: 0, labels: [] as string[] },
    };

    try {
        // Fetch all relevant file logs
        const queryParams = new URLSearchParams();
        if (unit?.id) {
            queryParams.append('unitId', unit.id);
        }

        const labels = [
            FileLabel.ElektriciteitKeuringsattestUnit,
            FileLabel.ElektriciteitKeuringsattestGebouw,
            FileLabel.ElektriciteitEendraadsschemaUnit,
            FileLabel.ElektriciteitEendraadsschemaGebouw,
            FileLabel.ElektriciteitSituatieschetsUnit,
            FileLabel.ElektriciteitSituatieschetsBuilding,
        ];
        queryParams.append('labels', labels.join(','));

        const response = await fetchWithAuth(
            `${API_URL}/file/get-file-logs?${queryParams.toString()}`,
            { method: 'GET' },
            session
        );
        const fileLogs = await response.json();
        const currentDate = new Date();

        // Track what we've already counted to avoid duplicates
        const counted = {
            certificate: false,
            schema: false,
            situationPlan: false,
        };

        // Check certificates
        const certificateLogs = fileLogs.filter((log: any) =>
            log.label.includes(FileLabel.ElektriciteitKeuringsattestUnit) ||
            log.label.includes(FileLabel.ElektriciteitKeuringsattestGebouw)
        ).sort((a: any, b: any) => new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime());

        // Find the most recent valid certificate
        const validCertificate = certificateLogs.find((cert: any) => {
            const expirationDate = cert.expirationDate ? new Date(cert.expirationDate) : null;
            return (cert.status === FileLogStatus.OK || cert.status === FileLogStatus.OK_WITH_REMARKS) 
                && (!expirationDate || expirationDate > currentDate);
        });

        if (!certificateLogs.length) {
            dotValues.red.count++;
            dotValues.red.labels.push("Geen Conform elektriciteitscertificaat aanwezig");
        } else if (!validCertificate) {
            dotValues.red.count++;
            dotValues.red.labels.push("Geen Conform elektriciteitscertificaat aanwezig");
        } else {
            const expirationDate = validCertificate.expirationDate ? new Date(validCertificate.expirationDate) : null;

            if (validCertificate.status === FileLogStatus.OK) {
                dotValues.green.count++;
                dotValues.green.labels.push("Conform elektriciteitscertificaat");
            } else { // must be OK_WITH_REMARKS since we filtered for these two statuses
                dotValues.orange.count++;
                dotValues.orange.labels.push("Conform elektriciteitscertificaat met opmerkingen");
            }

            // Check if it's about to expire
            if (expirationDate && expirationDate <= threeMonthsFromNow) {
                dotValues.orange.count++;
                dotValues.orange.labels.push("Elektriciteitscertificaat verloopt binnen drie maanden");
            }
        }

        // Check for schema
        const schemaLogs = fileLogs.filter((log: any) =>
            log.label.includes(FileLabel.ElektriciteitEendraadsschemaUnit) ||
            log.label.includes(FileLabel.ElektriciteitEendraadsschemaGebouw)
        ).sort((a: any, b: any) => new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime());

        if (!schemaLogs.length) {
            if (!counted.schema) {
                dotValues.red.count++;
                dotValues.red.labels.push("Geen elektriciteitsschema aanwezig");
                counted.schema = true;
            }
        } else if (!counted.schema) {
            const latestSchema = schemaLogs[0];
            if (latestSchema.status === FileLogStatus.OK) {
                dotValues.green.count++;
                dotValues.green.labels.push("Conform elektriciteitsschema aanwezig");
            } else if (latestSchema.status === FileLogStatus.NOT_REQUIRED) {
                dotValues.green.count++;
                dotValues.green.labels.push("Conform elektriciteitsschema aanwezig");
            } else if (latestSchema.status === FileLogStatus.OK_WITH_REMARKS) {
                dotValues.orange.count++;
                dotValues.orange.labels.push("Conform elektriciteitsschema aanwezig met opmerkingen");
            } else if (latestSchema.status === FileLogStatus.NOT_OK) {
                dotValues.red.count++;
                dotValues.red.labels.push("Ongeldig elektriciteitsschema");
            } else {
                dotValues.transparent.count++;
                dotValues.transparent.labels.push("Elektriciteitsschema aanwezig, maar onbekende status");
            }
            counted.schema = true;
        }

        // Check for situation plan
        const situationPlanLogs = fileLogs.filter((log: any) =>
            log.label.includes(FileLabel.ElektriciteitSituatieschetsUnit) ||
            log.label.includes(FileLabel.ElektriciteitSituatieschetsBuilding)
        ).sort((a: any, b: any) => new Date(b.documentDate).getTime() - new Date(a.documentDate).getTime());

        if (!situationPlanLogs.length) {
            if (!counted.situationPlan) {
                dotValues.red.count++;
                dotValues.red.labels.push("Geen situatieplan van de elektriciteit aanwezig");
                counted.situationPlan = true;
            }
        } else if (!counted.situationPlan) {
            const latestPlan = situationPlanLogs[0];
            if (latestPlan.status === FileLogStatus.OK) {
                dotValues.green.count++;
                dotValues.green.labels.push("Situatieplan van de elektriciteit aanwezig");
            } else if (latestPlan.status === FileLogStatus.NOT_REQUIRED) {
                dotValues.green.count++;
                dotValues.green.labels.push("Geldig situatieplan van de elektriciteit");
            } else if (latestPlan.status === FileLogStatus.OK_WITH_REMARKS) {
                dotValues.orange.count++;
                dotValues.orange.labels.push("Situatieplan van de elektriciteit aanwezig met opmerkingen");
            } else if (latestPlan.status === FileLogStatus.NOT_OK) {
                dotValues.red.count++;
                dotValues.red.labels.push("Ongeldig situatieplan van de elektriciteit");
            } else {
                dotValues.transparent.count++;
                dotValues.transparent.labels.push("Situatieplan van de elektriciteit aanwezig, maar onbekende status");
            }
            counted.situationPlan = true;
        }

        // Check meters
        if (!unit.electricityInfo?.electricityMeter?.length) {
            dotValues.transparent.count++;
            dotValues.transparent.labels.push("Geen elektriciteitsmeters geregistreerd");
        } else {
            const hasDigitalMeter = unit.electricityInfo.electricityMeter.some(
                (meter: any) => meter.meterType === MeterType.DIGITAL
            );
            
            if (hasDigitalMeter) {
                dotValues.green.count++;
                dotValues.green.labels.push("Alle elektriciteitsmeters zijn digitaal");
            } else {
                dotValues.orange.count++;
                dotValues.orange.labels.push("Niet alle elektriciteitsmeters zijn digitaal");
            }
        }

        // Check connection type
        if (!unit.electricityInfo?.electricityConnectedTo) {
            dotValues.transparent.count++;
            dotValues.transparent.labels.push("Onbekende aansluitingsstatus voor elektriciteit");
        } else if (unit.electricityInfo.electricityConnectedTo === ElectricityConnectedTo.PRIVATE) {
            dotValues.green.count++;
            dotValues.green.labels.push("Elektriciteit aangesloten op privé meter");
        } else if (unit.electricityInfo.electricityConnectedTo === ElectricityConnectedTo.COMMON) {
            dotValues.orange.count++;
            dotValues.orange.labels.push("Elektriciteit aangesloten op gemeenschappelijke meter");
        }

    } catch (error) {
        console.error('Error fetching file logs:', error);
    }

    return dotValues;
};
